import { StaticRefer } from '@src/slide/application/service/snap-line-service/context/refer/static';
import { DynamicRefer } from '@src/slide/application/service/snap-line-service/context/refer/dynamic';
import { observable } from 'mobx';
import {
    getFlippedAnchor,
    getMousePoint,
    getMovingMatchedOrient,
    getMovingOrientDirection,
    getOperationAnchorWithMode,
    getRotatedAnchor,
    predictMovingDirection,
} from '@src/slide/application/service/snap-line-service/util/movement';
import {
    MovementDirection,
    OperationAnchor,
} from '@src/slide/application/service/snap-line-service/interface/orient.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import {
    getGroupChildRelativeProps,
} from '@src/slide/domain/ui-model/element-modeles/property/group-operation/group-condition';
import {
    getNextFrameOnSlide,
    getDetectFrame,
} from '@src/slide/application/service/snap-line-service/util/frame';
import { MODES } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import { ADSORB_THRESHOLD } from '@src/slide/application/service/snap-line-service/constant';
import type { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import type { IResizingEventPayload } from '../../edit-action-services/resize-element-handler/resize-element-handler.interface';
import type {
    OperationType,
    IContextDetail,
    SnapLineContextOption,
    ProcessingEventPayload,
} from '@src/slide/application/service/snap-line-service/interface/snap-line-service.interface';
import type {
    IRelativeProps,
} from '@src/slide/domain/ui-model/element-modeles/property/group-operation/group-condition';
import type { IPoint } from '@src/slide/infrastructure/module/independent/graphic/point/point.interface';
import type { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import type { OrientDirection,
    Orient } from '@src/slide/application/service/snap-line-service/interface/orient.interface';
import type {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import type { IFrame } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import type { MouseEvent } from 'react';
import type { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import type { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import type { IActiveSnapGridLine } from '@src/slide/application/service/snap-line-service/interface/line.interface';
import type { IFlipInfo, ISizeInfo } from '@src/slide/application/service/edit-action-services/element-info';
import type {
    IEditAreaCanvasSizeManager,
} from '@src/slide/application/service/edit-area-canvas-size-manager/edit-area-canvas-size-manager.interface';

/**
 * 参考线上下文
 * 职责：
 *  1. 管理参照元素：部分参考线的参照物是所有元素，部分参考线的参照物是在水平、垂直方向的投影有重叠的元素。
 *  2. 管理参照元素网格化缓存，后续的判定逻辑基于几何网格进行判定
 *  3. 管理生效中的参考线
 *  4. 管理在操作过程中的一些变量如：移动方向、元素、尺寸等
 */
export class SnapLineContext implements IContextDetail {
    /** debug 专用变量 ↓↓↓↓↓↓ **/
    @observable public elementGrids: any[];
    @observable public detectGrids: any[];
    @observable public adsorbPoints: IPoint[];
    @observable public beforeAdsorbPoints: IPoint[];
    /** debug 专用变量 ↑↑↑↑↑↑ **/

    /**
     * 实际生效的吸附线，用于渲染吸附线和吸附计算
     */
    @observable public activeSnapLines: IActiveSnapGridLine[];
    /**
     * editProcess 上下文中的 offsetFrame 数据
     * 这些 editProcess 的上下文数据，先暂时融合到 context 中，便于访问
     * 实际使用到的都在 commiter中，不耦合其他逻辑。后续整体改造 editProcess的吸附时序，移除这些 editProcess的内容
     */
    public offsetFrame: any;
    /**
     * 画布缩放数据
     */
    public scale: number;
    /**
     * editProcess 的 sizeMap 数据
     */
    public sizeMap?: Map<string, any>;

    /**
     * 静态参照元素
     */
    public staticRefer: StaticRefer;
    /**
     * 动态参照元素
     */
    public dynamicRefer: DynamicRefer;
    /**
     * 上下文配置项
     */
    public option: SnapLineContextOption;
    /**
     * 操作中的元素
     */
    public element: IElementBaseModel;
    /**
     * 在画布上的rotate
     */
    public rotateOnSlide: number;
    /**
     * 操作元素为组合子元素时，相对父组合的旋转和翻转信息
     */
    public relativeProps: IRelativeProps;
    /**
     * 操作过程元素自身属性的 nextSize，由上层传递下来
     */
    public nextSize: IFrame;
    /**
     * 自身的nextSize，转换为在画布上的数据
     */
    public nextSizeOnSlide: IFrame;
    /**
     * 元素的 originalSize，由上层传递下来
     */
    public originalSize: IFrame;
    /**
     * 元素在画布上，进行吸附探测的 frame 数据
     */
    public detectFrame: IFrame;
    /**
     * 操作的原始 resizeNode 锚点数据，由 mode 直接映射而来
     */
    public originOperationAnchor: OperationAnchor;
    /**
     * 在当前操作下实时的锚点，用于判定后续操作的方向, 在当前slide上是什么方向的就是什么方向
     */
    public operationAnchorOnSlide: OperationAnchor;
    /**
     * 操作类型
     */
    public operationType: OperationType;
    /**
     * 操作开始的坐标，用于后续计算操作点位置，升成十字吸附线
     */
    public startMousePoint: IPoint;
    /**
     * 操作事件
     */
    public mouseEvent: MouseEvent;
    /**
     * 操作移动趋势
     */
    public movingDirection: MovementDirection;
    /**
     * 移动基础方向，只分垂直、水平
     */
    public movingOrient: Orient;
    /**
     * 移动基础方位
     */
    public movingOrientDirection: OrientDirection;
    /**
     * 元素是否锁定横纵比
     */
    public noChangeAspect: boolean;
    /**
     * 元素宽高比
     */
    public elementScale: number;

    public constructor(
        public selectionService: ISelectionService,
        public uiDataService: IUiDataService,
        private editAreaCanvasSizeManager: IEditAreaCanvasSizeManager,
        public menuManager: IMenuManager,
        option?: Partial<SnapLineContextOption>,
    ) {
        this.staticRefer = new StaticRefer(this);
        this.dynamicRefer = new DynamicRefer(this);
        this.option = { ...this.defaultOptions, ...option };
    }

    public init(element: IElementBaseModel, e: any) {
        this.setOperationElement(element);
        this.staticRefer.setRefWithSlideOtherElements();
        if (e) {
            this.startMousePoint = getMousePoint(e);
        }
    }

    public update(detectFrame: IFrame) {
        this.dynamicRefer.setRefWithShadowOverlappedElements(detectFrame);
    }

    public setDetail(payload: ProcessingEventPayload, operationType: OperationType) {
        this.offsetFrame = (payload as any).offsetFrame;

        const detail = this.getContextDetailFromPayload(payload, operationType);
        this.mouseEvent = detail.mouseEvent;
        this.nextSize = detail.nextSize;
        this.originalSize = detail.originalSize;
        this.detectFrame = detail.detectFrame;
        this.nextSizeOnSlide = detail.nextSizeOnSlide;
        this.originOperationAnchor = detail.originOperationAnchor;
        this.operationAnchorOnSlide = detail.operationAnchorOnSlide;
        this.operationType = detail.operationType;
        this.sizeMap = detail.sizeMap;
        this.scale = detail.scale;
        this.elementScale = detail.elementScale;
        this.rotateOnSlide = detail.rotateOnSlide;
        this.noChangeAspect = detail.noChangeAspect;
        // 如果计算出来是原地，就按移动过程中上一次的趋势判定
        if (detail.movingDirection !== MovementDirection.NONE || !this.movingDirection) {
            this.movingDirection = detail.movingDirection;
            this.movingOrient = getMovingMatchedOrient(this.mouseEvent);
            this.movingOrientDirection = getMovingOrientDirection(this.mouseEvent);
        }
    }

    public reset() {
        this.activeSnapLines = [];
        this.dynamicRefer.reset();
        this.staticRefer.reset();
        this.resetDetail();

        // TODO 测试用代码，待移除
        this.elementGrids = [];
        this.detectGrids = [];
        this.beforeAdsorbPoints = [];
        this.adsorbPoints = [];
    }

    public setOperationElement(element: IElementBaseModel) {
        this.element = element;
        this.relativeProps = getGroupChildRelativeProps(element);
    }

    public setActiveSnapLines(snapLines: IActiveSnapGridLine[], frameOnSlide?: IFrame) {
        this.activeSnapLines = snapLines.map(snapLine => ({
            ...snapLine,
            frameOnSlide,
        }));
    }

    public get elementRotate() {
        // 优先使用 nextSize 中的 rotate
        if (this.nextSize.rotate !== undefined) {
            return this.nextSize.rotate;
        }

        const { rotate = 0 } = this.element?.props?.style || {};
        return rotate;
    }

    public get groupRotate() {
        return (this.isGroupChildren && this.parentStyle?.rotate) || 0;
    }

    public get isGroupChildren() {
        return this.element?.parent?.type === UIModelType.GROUP;
    }

    public get parentStyle() {
        return (this.element?.parent as IGroupModel)?.props.style;
    }

    private getContextDetailFromPayload(payload: ProcessingEventPayload, operationType: OperationType): IContextDetail {
        const { mode, originalSize, nextSize,
            multiRect, elementModel, e } = payload;

        const movingDirection = predictMovingDirection(e);
        const isGroupChildren = elementModel?.parent?.type === UIModelType.GROUP;
        const nextSizeOnSlide = isGroupChildren
            ? getNextFrameOnSlide(elementModel, originalSize, nextSize)
            : { rotate: elementModel.props.style.rotate, ...nextSize };
        const detectFrame = getDetectFrame(
            operationType,
            elementModel,
            originalSize as ISizeInfo & IFlipInfo,
            nextSize as ISizeInfo & IFlipInfo,
            multiRect,
        );

        // TODO OperationAnchor 这里代码做一些简化和调整
        const originOperationAnchor = getOperationAnchorWithMode(mode);
        let operationAnchorOnSlide = OperationAnchor.ALL;
        const rotateOnSlide = elementModel.getFrame().frameOnSlide().rotate ?? 0;
        if (mode !== MODES.MOVE) {
            operationAnchorOnSlide = this.getOperationAnchorOnSlide(
                originOperationAnchor,
                nextSize,
                rotateOnSlide,
            );
        }

        return ({
            mouseEvent: e,
            originOperationAnchor,
            operationAnchorOnSlide,
            nextSize,
            originalSize,
            detectFrame,
            nextSizeOnSlide,
            movingDirection,
            operationType,
            sizeMap: (payload as IResizingEventPayload).sizeMap,
            scale: this.editAreaCanvasSizeManager.getCanvasScale(),
            rotateOnSlide,
            noChangeAspect: !!(e?.shiftKey || elementModel.props.locks?.noChangeAspect),
            elementScale: originalSize.height / originalSize.width,
        });
    }

    private get defaultOptions() {
        return {
            maxAdsorbGap: ADSORB_THRESHOLD,
        };
    }

    private resetDetail() {
        this.offsetFrame = undefined;
        this.sizeMap = undefined;
        this.element = undefined as any;
        this.rotateOnSlide = undefined as any;
        this.nextSize = undefined as any;
        this.detectFrame = undefined as any;
        this.originOperationAnchor = undefined as any;
        this.operationAnchorOnSlide = undefined as any;
        this.operationType = undefined as any;
        this.mouseEvent = undefined as any;
        this.startMousePoint = undefined as any;
        this.movingDirection = undefined as any;
        this.movingOrient = undefined as any;
        this.movingOrientDirection = undefined as any;
        this.noChangeAspect = undefined as any;
        this.elementScale = undefined as any;
    }

    private getSelfOperateFlipInfo(nextSize: IFrame) {
        const { element } = this;
        const { flipV: originFlipV, flipH: originFlipH } = element.props.style;
        // 1. 在操作过程中的 flip，会出现 mode是 left，但是是往右拉的情况 2. 父元素有 flip的时候，会影响 mode的方向
        // 对于自己只有在原 flip 和 操作过程中的时候 flip 不一样才需要做锚点变换。不然就用根据 Mode 的锚点进行判定
        let flipH = !!originFlipH !== !!nextSize.flipH;
        let flipV = !!originFlipV !== !!nextSize.flipV;

        // // 如果画布上和原本的翻转不同，则是上层元素还有翻转，就再进行翻转
        const { relativeFlipH, relativeFlipV } = this.relativeProps;
        if (relativeFlipH) {
            flipH = !flipH;
        }
        if (relativeFlipV) {
            flipV = !flipV;
        }

        return { flipV, flipH };
    }

    /**
     * 结合翻转，获取在当前操作下实时的锚点，用于判定后续操作的方向
     * 此方法着重于预测当前拖动锚点的倾向，结合了父与自身的实时翻转，在当前slide上是什么方向的就是什么方向
     */
    private getOperationAnchorOnSlide(originAnchor: OperationAnchor, nextSize: IFrame, rotateOnSlide: number) {
        const { flipV, flipH } = this.getSelfOperateFlipInfo(nextSize);
        const flippedAnchor = getFlippedAnchor(originAnchor, flipH, flipV);

        return getRotatedAnchor(flippedAnchor, rotateOnSlide);
    }
}
